import getConfig from "next/config"
import Head from "next/head"
import Router from "next/router"
import React, { useEffect, useRef, useState } from "react"
import styled from "styled-components"

import { Header } from "~/app/Free/Header"
import { GameCatalogNew } from "~/app/Renew/GameCatalog"
import { Note } from "~/app/Renew/Note"
import { AdBanner, AdRectangle } from "~/components/Ad"
import { Microroad } from "~/components/Ad/Microroad"
import { Footer } from "~/components/Footer"
import { PageTop } from "~/components/PageTop"
import { adIds, distribution, microroadAdId } from "~/config"
import { ExperimentProvider } from "~/contexts/Experiment"
import { TrackingProvider } from "~/contexts/Tracking"
import { Fetcher } from "~/external/Fetcher"
import { useRepro } from "~/hooks/useRepro"
import { mixin } from "~/libs/style"
import { ConfigPresenter } from "~/presenter/ConfigPresenter"
import { GameCatalogsPresenter } from "~/presenter/GameCatalogsPresenter"
import { ConfigRepository } from "~/repository/ConfigRepository"
import { GameCatalogsRepository } from "~/repository/GameCatalogsRepository"
import { GenresRepository } from "~/repository/GenresRepository"

type ListPageProps = Pick<ReturnType<typeof getStaticProps> extends Promise<infer R> ? R : never, "props">

const ListPage: React.FC<ListPageProps["props"]> = (props) => {
  const [rankingOffset, setRankingOffset] = useState(500)
  const gamecatalogRef = useRef<HTMLDivElement>(null)
  const { distribution, adIds, config, configDomain, genres, gameCatalogs, microroadAdId } = props
  const adConfig = adIds[distribution].list
  const adRect = adConfig.rectangle
  const adBanner = adIds[distribution].banner
  const baseUrl = configDomain.free
  const adInfeedId = adConfig.infeed
  useRepro(distribution)
  useEffect(() => {
    if (distribution !== "portal" && distribution !== "free") {
      Router.push("/")
    }

    const offsetAfterMount = gamecatalogRef?.current?.offsetTop
    offsetAfterMount && setRankingOffset(offsetAfterMount)
  }, [rankingOffset])

  return (
    <ExperimentProvider configExperiments={config.experiments}>
      <Head>
        <link rel="canonical" href={`${baseUrl}/ranking`} />
        <meta name="description" content="イージーゲーム（簡単ゆる）人気ゲームランキング" />
        <meta name="keywords" content="無料ゲーム,無料,ゲーム,スマホゲーム,簡単ゲーム,カジュアルゲーム" />
        <meta name="apple-mobile-web-app-title" content="イージーゲーム（簡単ゆる）ランキング" />
        <title>人気ゲームランキング - イージーゲーム（簡単ゆる）|au Webポータル</title>
      </Head>
      <TrackingProvider categoryProp="人気ゲームランキング">
        <Container>
          <Header hasBackButton={true} backUrl={configDomain.free} />
          <AdBanner id={adBanner.id} url={adBanner.url} size={adBanner.size} />
          <Wrapper ref={gamecatalogRef}>
            {
              <GameCatalogNew
                gameCatalogs={gameCatalogs}
                genres={genres}
                adInfeedId={adInfeedId}
                showAll={true}
                isGameListPage={true}
              />
            }
          </Wrapper>
          <Note />
          <AdRectangle id={adRect.id} url={adRect.url} size={adRect.size} />
          <PageTop displayOffset={rankingOffset} />
          <Footer />
          <Microroad id={microroadAdId} />
        </Container>
      </TrackingProvider>
    </ExperimentProvider>
  )
}

const Container = styled.div`
  background: #f1f1f1;
  padding-bottom: 50px;
  @media (orientation: portrait) {
    padding-bottom: calc(50 * (100vw / 320));
  }
`

const Wrapper = styled.section`
  background: #ffffff;
  margin: 8px auto 0;
  ${mixin.maxWidth}
  box-sizing: border-box;
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
`

export const getStaticProps = async () => {
  const {
    serverRuntimeConfig: { cmsEndpoint, cmsApiKey, configDomain }
  } = getConfig()

  const fetcher = new Fetcher({ cmsEndpoint, cmsApiKey })

  const configRepo = new ConfigRepository(fetcher)
  const config = await configRepo.findAll()

  const genresRepo = new GenresRepository(fetcher)
  const genres = await genresRepo.findAll()

  const gameCatalogsRepo = new GameCatalogsRepository(fetcher)
  const gameCatalogs = await gameCatalogsRepo.findAll()

  return {
    props: {
      distribution,
      adIds,
      config: ConfigPresenter.transform(config),
      configDomain: configDomain,
      genres,
      gameCatalogs: GameCatalogsPresenter.transform(gameCatalogs),
      microroadAdId
    }
  }
}

export default ListPage
